import { ChangeDetectionStrategy, Component, ElementRef, HostListener } from "@angular/core";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { PersonDetail, PersonDetailNames } from "@common/ADAPT.Common.Model/person/person-detail";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { UserService } from "@common/user/user.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { OrganisationService } from "@org-common/lib/organisation/organisation.service";
import { BehaviorSubject, filter, finalize, of, startWith, switchMap, tap } from "rxjs";
import { ActivityFeedService } from "../activity-feed.service";
import { ActivityFeedSignalRHub } from "../activity-feed-signalr-hub";

@Component({
    selector: "adapt-activity-notification",
    templateUrl: "./activity-notification.component.html",
    styleUrl: "./activity-notification.component.css",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ActivityNotificationComponent extends BaseComponent {
    public unreadCountText = "";

    private currentPerson?: Person;
    private activityFeedTimestamp?: Date;
    private triggerUpdate$ = new BehaviorSubject<void>(undefined);
    private updateTimestampOnClick = false;

    public constructor(
        private activityFeedService: ActivityFeedService,
        activityFeedHub: ActivityFeedSignalRHub,
        userService: UserService,
        elementRef: ElementRef,
        rxjsBreezeService: RxjsBreezeService,
        orgService: OrganisationService,
    ) {
        super(elementRef);
        this.triggerUpdate$.pipe(
            switchMap(() => orgService.promiseToWaitForOrganisationReady()),
            switchMap(() => userService.currentPerson$),
            filter((person) => !!person),
            tap((person) => this.currentPerson = person),
            switchMap((person) => this.activityFeedService.getPersonalActivityTimestamp(person)),
            tap((timestamp) => {
                this.activityFeedTimestamp = timestamp;
                if (timestamp) {
                    this.activityFeedService.emitActivityFeedTimestampUpdated(timestamp);
                }
            }),
            switchMap(() => activityFeedHub.onPersonalActivity(this.currentPerson!).pipe(
                startWith(undefined), // this is to trigger fetch on startup to update the startup count
            )),
            // trigger fetch when new activity arrives
            switchMap(() => this.fetchNewerActivity()),
            this.takeUntilDestroyed(),
        ).subscribe();

        rxjsBreezeService.entityTypeChanged(PersonDetail).pipe(
            filter((personDetail) => !this.updateTimestampOnClick &&
                personDetail.personId === this.currentPerson?.personId &&
                personDetail.name === PersonDetailNames.ActivityFeedTimestamp),
            this.takeUntilDestroyed(),
        ).subscribe(() => this.triggerUpdate$.next());
    }

    @HostListener("click")
    public onClick() {
        // won't update this timestamp if there is no unread activity
        if (this.unreadCountText) {
            this.unreadCountText = "";
            this.updateTimestampOnClick = true;
            this.activityFeedService.updatePersonalActivityTimestamp(this.currentPerson!).pipe(
                this.takeUntilDestroyed(),
                finalize(() => this.updateTimestampOnClick = false),
            ).subscribe();
        }
    }

    private fetchNewerActivity() {
        if (this.currentPerson) {
            const getActivityObs = this.activityFeedTimestamp
                ? this.activityFeedService.getNewerPersonalActivity(this.currentPerson, this.activityFeedTimestamp)
                : this.activityFeedService.getInitialPersonalActivity(this.currentPerson);
            return getActivityObs.pipe(
                tap((activityInstances) => {
                    activityInstances = activityInstances
                        // only interested in recent activity
                        .filter((activityInstance) => activityInstance.recentlyTriggered);
                    this.unreadCountText = activityInstances.length > 0
                        ? (activityInstances.length < 10 ? `${activityInstances.length}` : "9+")
                        : "";
                }),
            );
        }

        return of(undefined);
    }
}
